import { useCallback, useRef } from 'react';

export interface UseTypeaheadOptions {
  /** Searchable text for each item, in list order. */
  labels: () => string[];
  /** Called with the index of the first match. */
  onMatch: (index: number) => void;
  /** Index to search from (the search wraps and starts just after it). */
  from?: () => number;
  /** How long typed characters accumulate before the buffer resets. */
  timeout?: number;
  isDisabled?: (index: number) => boolean;
}

/**
 * Listbox type-ahead: typing "de" jumps to "Delta", repeating one character cycles through the
 * items starting with it. Used by Select and MultiSelect, which have no text input to type into.
 */
export function useTypeahead({ labels, onMatch, from, timeout = 600, isDisabled }: UseTypeaheadOptions) {
  const buffer = useRef('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(
    (char: string): boolean => {
      if (char.length !== 1 || char === ' ') return false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        buffer.current = '';
      }, timeout);

      const repeated = buffer.current.length > 0 && buffer.current.split('').every((c) => c === char);
      buffer.current += char;
      const needle = (repeated ? char : buffer.current).toLowerCase();

      const all = labels();
      if (all.length === 0) return false;
      const start = (from?.() ?? -1) + 1;
      for (let i = 0; i < all.length; i += 1) {
        const index = (start + i) % all.length;
        if (isDisabled?.(index)) continue;
        if ((all[index] ?? '').toLowerCase().startsWith(needle)) {
          onMatch(index);
          return true;
        }
      }
      return false;
    },
    [labels, onMatch, from, timeout, isDisabled],
  );
}
